import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Loader2, Mail, Stethoscope, UserMinus, UserPlus } from "lucide-react";
import { Badge } from "@app/components/ui/badge";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import {
  isOrganisationDetail,
  isOrganisationsResponse,
  personName,
  type OrganisationDetail,
  type OrganisationRole,
} from "@app/components/organisation/types";
import { useOrganisationMessages } from "@app/components/organisation/useOrganisationMessages";
import API from "@app/services/api";
import type { SpecialityDescriptor } from "@app/utils/speciality";
import { specialityLabel } from "@app/utils/speciality";
import useUser from "@app/zustand/user";

interface OrganisationMembersProps {
  detail: OrganisationDetail;
  /** Shown as its gestionnaire sees it, with no control: the demo's fictitious organisation. */
  readOnly?: boolean;
  /** The organisation changed: its new state. */
  onChanged: (detail: OrganisationDetail) => void;
  /** The caller left it. */
  onLeft: () => void;
}

interface InviteResponse {
  email_sent: boolean;
}

function isInviteResponse(res: unknown): res is InviteResponse {
  return (
    typeof res === "object" &&
    res !== null &&
    typeof (res as InviteResponse).email_sent === "boolean"
  );
}

/**
 * Who belongs to the organisation, and who is invited (PT-119).
 *
 * A gestionnaire sees everyone, invites, changes roles and removes; a membre sees the gestionnaires and
 * may leave. An organisation always keeps one gestionnaire — the server says so when it is asked
 * otherwise.
 */
export default function OrganisationMembers({
  detail,
  readOnly = false,
  onChanged,
  onLeft,
}: OrganisationMembersProps) {
  const { t } = useLingui();
  const { failureMessage } = useOrganisationMessages();
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrganisationRole>("membre");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const organisationId = detail.organisation.id;
  const isManager = detail.role === "gestionnaire";
  const canManage = isManager && !readOnly;
  // MAGE-X's own organisation: belonging to it opens the speciality, so only an admin changes it —
  // leaving included (the server refuses anyone else).
  const isPublisher = detail.organisation.type === "editeur";
  // Which specialities each of MAGE-X's reviewers corrects: their own, and those an admin granted
  // them (PT-137). The REAL role decides who grants — the server refuses anybody else, eye or not.
  const user = useUser((state) => state.user);
  const canGrant =
    isPublisher &&
    !readOnly &&
    (user as { role?: string } | null)?.role === "admin";
  const [specialites, setSpecialites] = useState<SpecialityDescriptor[]>([]);

  useEffect(() => {
    if (!isPublisher) return;
    let cancelled = false;
    API.get({ path: "/user/early-user-specialites" }).then((res: unknown) => {
      const list = (res as { data?: { specialites?: SpecialityDescriptor[] } })
        ?.data?.specialites;
      if (!cancelled && Array.isArray(list)) setSpecialites(list);
    });
    return () => {
      cancelled = true;
    };
  }, [isPublisher]);

  const labelOf = (id: string) =>
    specialityLabel(
      id,
      t,
      specialites.find((sp) => sp.id === id),
    );

  const roleLabel = (role: OrganisationRole) =>
    role === "gestionnaire" ? t`Gestionnaire` : t`Membre`;

  function refresh() {
    API.get({ path: `/organisation/${organisationId}` }).then(
      (res: unknown) => {
        if (isOrganisationDetail(res)) onChanged(res);
      },
    );
  }

  function run(
    key: string,
    request: Promise<unknown>,
    onDone: (res: unknown) => void,
  ) {
    setBusy(key);
    setError("");
    setNotice("");
    request
      .then((res: unknown) => {
        if (
          isOrganisationDetail(res) ||
          isOrganisationsResponse(res) ||
          isInviteResponse(res)
        ) {
          onDone(res);
        } else {
          setError(failureMessage(res, t`L'action a échoué. Réessayez.`));
        }
      })
      .finally(() => setBusy(null));
  }

  function invite() {
    const address = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(address)) {
      setError(t`L'adresse e-mail n'est pas valide.`);
      return;
    }
    run(
      "invite",
      API.post({
        path: `/organisation/${organisationId}/invitations`,
        body: { email: address, role: inviteRole },
      }),
      (res) => {
        setEmail("");
        setNotice(
          isInviteResponse(res) && res.email_sent
            ? t`Invitation envoyée à ${address}.`
            : t`Invitation enregistrée pour ${address}, mais l'e-mail n'a pas pu partir : prévenez la personne vous-même.`,
        );
        refresh();
      },
    );
  }

  function revoke(invitationId: string) {
    run(
      `revoke-${invitationId}`,
      API.delete({
        path: `/organisation/${organisationId}/invitations/${invitationId}`,
      }),
      (res) => isOrganisationDetail(res) && onChanged(res),
    );
  }

  function changeRole(memberId: string, role: OrganisationRole) {
    run(
      `role-${memberId}`,
      API.put({
        path: `/organisation/${organisationId}/members/${memberId}`,
        body: { role },
      }),
      (res) => isOrganisationDetail(res) && onChanged(res),
    );
  }

  function grant(memberId: string, granted: string[]) {
    run(
      `grant-${memberId}`,
      API.put({
        path: `/organisation/${organisationId}/members/${memberId}/specialites`,
        body: { specialites: granted },
      }),
      (res) => isOrganisationDetail(res) && onChanged(res),
    );
  }

  function remove(memberId: string, isMe: boolean) {
    const confirmation = isMe
      ? t`Quitter cette organisation ?`
      : t`Retirer ce membre de l'organisation ?`;
    if (!window.confirm(confirmation)) return;
    run(
      `remove-${memberId}`,
      API.delete({
        path: `/organisation/${organisationId}/members/${memberId}`,
      }),
      () => (isMe ? onLeft() : refresh()),
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Membres</Trans>
        </CardTitle>
        <CardDescription>
          {isPublisher ? (
            <Trans>
              L'organisation de l'éditeur : ses médecins relisent et corrigent
              toute leur spécialité, et celles qu'un administrateur leur
              accorde. Seul un administrateur MAGE-X en gère les membres.
            </Trans>
          ) : isManager ? (
            <Trans>
              Les comptes rattachés à l'organisation. Être membre ne donne accès
              à aucun dossier patient : chacun garde le sien.
            </Trans>
          ) : (
            <Trans>Les gestionnaires de l'organisation.</Trans>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 text-sm">
          {detail.members.map((member) => (
            <li
              key={member.id}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
            >
              {/* Grows and wraps its own text, so a long « Corrige » line keeps the actions beside it. */}
              <span className="min-w-[240px] flex-1 text-slate-900">
                {personName(member)}
                <span className="ml-2 text-slate-500">{member.email}</span>
                {member.is_me && (
                  <Badge variant="secondary" className="ml-2">
                    <Trans>Vous</Trans>
                  </Badge>
                )}
                {isPublisher && member.specialite && (
                  <span className="mt-0.5 block text-xs text-slate-500">
                    <Trans>Corrige :</Trans>{" "}
                    {[member.specialite, ...(member.granted_specialites ?? [])]
                      .map(labelOf)
                      .join(" · ")}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {canGrant && member.specialite && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy !== null}
                        aria-label={t`Spécialités corrigées par ${personName(member)}`}
                      >
                        <Stethoscope className="h-4 w-4" />
                        <span className="ml-1">
                          <Trans>Spécialités</Trans>
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel>
                        <Trans>Accorder en plus de la sienne</Trans>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {specialites
                        .filter((sp) => sp.id !== member.specialite)
                        .map((sp) => {
                          const granted = member.granted_specialites ?? [];
                          const checked = granted.includes(sp.id);
                          return (
                            <DropdownMenuCheckboxItem
                              key={sp.id}
                              checked={checked}
                              disabled={busy !== null}
                              // Kept open: an admin usually sets several at once.
                              onSelect={(event) => event.preventDefault()}
                              onCheckedChange={() =>
                                grant(
                                  member.id,
                                  checked
                                    ? granted.filter((id) => id !== sp.id)
                                    : [...granted, sp.id],
                                )
                              }
                            >
                              {labelOf(sp.id)}
                            </DropdownMenuCheckboxItem>
                          );
                        })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {canManage ? (
                  <Select
                    value={member.role}
                    onValueChange={(value) =>
                      changeRole(member.id, value as OrganisationRole)
                    }
                    disabled={busy !== null}
                  >
                    <SelectTrigger
                      className="h-8 w-[150px]"
                      aria-label={t`Rôle de ${personName(member)}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gestionnaire">
                        {roleLabel("gestionnaire")}
                      </SelectItem>
                      <SelectItem value="membre">
                        {roleLabel("membre")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-slate-600">
                    {roleLabel(member.role)}
                  </span>
                )}
                {!readOnly && (isManager || (member.is_me && !isPublisher)) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(member.id, member.is_me)}
                    disabled={busy !== null}
                    aria-label={
                      member.is_me
                        ? t`Quitter l'organisation`
                        : t`Retirer ${personName(member)}`
                    }
                  >
                    <UserMinus className="h-4 w-4" />
                    <span className="ml-1">
                      {member.is_me ? (
                        <Trans>Quitter</Trans>
                      ) : (
                        <Trans>Retirer</Trans>
                      )}
                    </span>
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>

        {isManager && (
          <div className="space-y-3 border-t pt-4">
            <h3 className="text-sm font-medium text-slate-900">
              {canManage ? (
                <Trans>Inviter un collègue</Trans>
              ) : (
                <Trans>Invitations en attente</Trans>
              )}
            </h3>
            {canManage && (
              <>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[240px] flex-1 space-y-1">
                    <Label htmlFor="organisation-invite-email">
                      <Trans>Adresse e-mail</Trans>
                    </Label>
                    <Input
                      id="organisation-invite-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t`Ex. : confrere@cabinet.fr`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="organisation-invite-role">
                      <Trans>Rôle</Trans>
                    </Label>
                    <Select
                      value={inviteRole}
                      onValueChange={(value) =>
                        setInviteRole(value as OrganisationRole)
                      }
                    >
                      <SelectTrigger
                        id="organisation-invite-role"
                        className="w-[150px]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="membre">
                          {roleLabel("membre")}
                        </SelectItem>
                        <SelectItem value="gestionnaire">
                          {roleLabel("gestionnaire")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={invite} disabled={busy !== null}>
                    {busy === "invite" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="mr-2 h-4 w-4" />
                    )}
                    <Trans>Inviter</Trans>
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  <Trans>
                    La personne accepte l'invitation depuis Configuration →
                    Organisation, une fois connectée avec cette adresse. Sans
                    compte MAGE-X, l'invitation l'attend jusqu'à l'ouverture de
                    son compte.
                  </Trans>
                </p>
              </>
            )}

            {detail.invitations.length > 0 && (
              <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 text-sm">
                {detail.invitations.map((invitation) => (
                  <li
                    key={invitation.id}
                    className="flex items-center justify-between gap-2 px-3 py-2"
                  >
                    <span className="flex items-center gap-2 text-slate-700">
                      <Mail className="h-4 w-4 text-slate-400" />
                      {invitation.email}
                      <span className="text-slate-500">
                        · {roleLabel(invitation.role)} ·{" "}
                        <Trans>en attente</Trans>
                      </span>
                    </span>
                    {canManage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revoke(invitation.id)}
                        disabled={busy !== null}
                      >
                        <Trans>Annuler l'invitation</Trans>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {notice && <p className="text-sm text-primary">{notice}</p>}
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
